import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert } from '@/components/ui/alert';
import { Plus, FileText, Eye, EyeOff, Menu, X } from 'lucide-react';
import { DraftFormsList } from '@/features/public/components/DraftFormsList';
import { LanguageSelector } from '@/components/LanguageSelector';
import { BusinessMaterialsCard } from '@/components/BusinessMaterialsCard';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useTranslation } from 'react-i18next';
import { useToastNotifications } from '@/components/ui/toast-notification';

export function Dashboard() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { addNotification } = useToastNotifications();
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showForms, setShowForms] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const headerRef = useRef<HTMLElement>(null);

  // Detekce úspěšného odeslání formuláře z URL parametru
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('raynetSubmitted') === 'true') {
      addNotification('success', 'Dotazník byl úspěšně odeslán do Raynetu!', undefined, 8000);
      // Vyčištění URL parametru
      navigate('/dashboard', { replace: true });
    }
  }, [location.search, addNotification, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMobileMenuOpen]);

  // Fetch user stats
  const { data: statsData } = useQuery({
    queryKey: ['user-dashboard-stats', user?.id],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/dashboard/stats?userId=${user?.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      return response.json();
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch user forms
  const { data: formsData } = useQuery({
    queryKey: ['user-forms', user?.id],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/dashboard/forms?userId=${user?.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch forms');
      return response.json();
    },
    enabled: !!user?.id && showForms,
    refetchInterval: 30000,
  });

  const handleContinueForm = (formId: string) => {
    navigate(`/form?formId=${formId}`);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      setPasswordError(t('dashboard.password_min_length'));
      return;
    }

    setIsChangingPassword(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user?.id,
          newPassword,
        }),
      });

      if (!response.ok) {
        throw new Error(t('dashboard.password_change_error'));
      }

      setPasswordSuccess(t('dashboard.password_change_success'));
      setNewPassword('');
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : t('dashboard.password_change_error'));
    } finally {
      setIsChangingPassword(false);
    }
  };


  const stats = statsData?.data;
  const forms = formsData?.data || [];

  return (
    <div className="min-h-screen bg-background">
      <header ref={headerRef} className="bg-card shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <img 
                src="/images/blaze-harmony-logo-2025.svg" 
                alt="Blaze Harmony Logo" 
                className="h-10 w-auto"
              />
              <div className="hidden sm:block">
                <p className="text-sm text-gray-600">
                  {t('dashboard.logged_in_as', { name: user?.full_name, username: user?.username })}
                </p>
              </div>
            </div>
            
            {/* Desktop menu */}
            <div className="hidden lg:flex items-center space-x-4">
              <LanguageSelector />
              <ThemeToggle />
              <Button
                variant="outline"
                onClick={() => setShowPasswordChange(!showPasswordChange)}
              >
                {t('dashboard.change_password')}
              </Button>
              {user?.is_admin && (
                <Button
                  variant="outline"
                  onClick={() => window.open('/admin', '_blank')}
                >
                  {t('dashboard.admin')}
                </Button>
              )}
              <Button variant="outline" onClick={logout}>
                {t('dashboard.logout')}
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {isMobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-200 py-4">
              <div className="sm:hidden mb-4 px-2">
                <p className="text-sm text-gray-600">
                  {t('dashboard.logged_in_as', { name: user?.full_name, username: user?.username })}
                </p>
              </div>
              <div className="flex flex-col space-y-3">
                <div className="px-2">
                  <LanguageSelector />
                </div>
                <div className="px-2">
                  <ThemeToggle />
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordChange(!showPasswordChange);
                    setIsMobileMenuOpen(false);
                  }}
                  className="justify-start"
                >
                  {t('dashboard.change_password')}
                </Button>
                {user?.is_admin && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      window.open('/admin', '_blank');
                      setIsMobileMenuOpen(false);
                    }}
                    className="justify-start"
                  >
                    {t('dashboard.admin')}
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="justify-start"
                >
                  {t('dashboard.logout')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Password change form */}
        {showPasswordChange && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('dashboard.password_change_title')}</CardTitle>
              <CardDescription>
                {t('dashboard.password_change_description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t('dashboard.new_password')}</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('dashboard.new_password_placeholder')}
                    disabled={isChangingPassword}
                  />
                </div>

                {passwordError && (
                  <Alert variant="destructive">
                    {passwordError}
                  </Alert>
                )}

                {passwordSuccess && (
                  <Alert>
                    {passwordSuccess}
                  </Alert>
                )}

                <div className="flex space-x-2">
                  <Button
                    type="submit"
                    disabled={isChangingPassword || !newPassword}
                  >
                    {isChangingPassword ? t('dashboard.changing') : t('buttons.save')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPasswordChange(false)}
                  >
                    {t('buttons.cancel')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* New form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                {t('dashboard.new_form_title')}
              </CardTitle>
              <CardDescription>
                {t('dashboard.new_form_description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate('/form')}
                className="w-full"
                size="lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('dashboard.create_form')}
              </Button>
            </CardContent>
          </Card>

          {/* My forms */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('dashboard.my_forms_title')}
              </CardTitle>
              <CardDescription>
                {t('dashboard.my_forms_description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setShowForms(!showForms)}
                variant={showForms ? "outline" : "default"}
                className="w-full"
                size="lg"
              >
                {showForms ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    {t('dashboard.hide_forms')}
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    {t('dashboard.show_forms')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Business Materials */}
          <BusinessMaterialsCard />
        </div>

        {/* Dashboard stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.stats_total_forms')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.totalForms || 0}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.stats_draft')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.draftForms || 0}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.stats_submitted')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.submittedForms || 0}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.stats_error')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.errorForms || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Forms list */}
        {showForms && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('dashboard.forms_title')}
              </CardTitle>
              <CardDescription>
                {t('dashboard.forms_description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {forms.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg font-medium mb-2">{t('dashboard.no_forms')}</p>
                  <p className="text-sm mb-4">{t('dashboard.no_forms_description')}</p>
                  <Button onClick={() => navigate('/form')}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('dashboard.create_form')}
                  </Button>
                </div>
              ) : (
                <DraftFormsList 
                  onContinueForm={handleContinueForm}
                  onClose={() => setShowForms(false)}
                  externalForms={forms}
                />
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
} 